import MailOutlineIcon from "@mui/icons-material/MailOutline";
import Divider from "@mui/material/Divider";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { Box, CardHeader, Chip, Drawer, IconButton, Typography } from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import CloseIcon from "@mui/icons-material/Close";
import ColoredChips from "shared-ui/src/theme/extensions/ColoredChips";
import { categoryColors, severityLevelColors } from "../utils/newsCenterColors";
import { useTheme } from "@mui/material/styles";
import { NewsCenterTheme } from "../utils/NewsCenterTheme";
import NewsCenterHeader from "@features/news-center-feature/components/NewsCenterHeader";
import DOMPurify from "dompurify";
import parse, { DOMNode, Element } from "html-react-parser";
import useGetNewsArticleDetail from "@features/news-center-feature/hooks/useGetNewsArticleDetail";
import { ShiSpinnerIcon } from "shared-ui/src/components/ShiSpinnerIcon";
import { DateTime } from "luxon";
import { fromNow } from "shared-ui/src/functions/convertDate";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import { NavigationDirection } from "@features/news-center-feature/utils/newsCenterUtils";

const replaceLinksWithNewTab = (domNode: DOMNode) => {
  if (!(domNode instanceof Element)) {
    return;
  }
  if (domNode.name === "a" && domNode.attribs.href) {
    domNode.attribs.target = "_blank";
  }
  if (domNode.name === "img" && domNode.attribs.src) {
    return (
      <Zoom>
        <img {...domNode.attribs} alt={domNode.attribs.alt || "Image"} />
      </Zoom>
    );
  }
};

interface ItemDetailDrawerProps {
  articleId: number;
  setArticleId: (val: number) => void;
  drawerIsOpen: boolean;
  navigateArticles: (direction: NavigationDirection) => void;
}

export default function ItemDetailDrawer({
  articleId,
  setArticleId,
  drawerIsOpen,
  navigateArticles,
}: Readonly<ItemDetailDrawerProps>) {
  const theme = useTheme();
  const styles = NewsCenterTheme(theme);

  const { data: article, isLoading } = useGetNewsArticleDetail(articleId);

  const getModifiedTime = () => {
    if (article?.modified !== undefined) {
      return fromNow(article.modified.toISOString());
    }
    return DateTime.now().toString();
  };

  const sanitizedContent = DOMPurify.sanitize(
    article?.content ?? "<div>No Content</div>",
  );

  const closeDrawer = () => {
    setArticleId(-1);
  };

  return (
    <Drawer open={drawerIsOpen} onClose={closeDrawer} anchor="right">
      <Box sx={{ width: "50vw" }}>
        <Box bgcolor="table.header">
          <CardHeader
            avatar={<MailOutlineIcon color="action" />}
            title={"News & Updates"}
            action={
              <Grid container gap={3}>
                <IconButton
                  aria-label="arrow-up"
                  onClick={() => navigateArticles("Up")}
                >
                  <ArrowUpwardIcon color="action" />
                </IconButton>
                <IconButton
                  aria-label="arrow-down"
                  onClick={() => navigateArticles("Down")}
                >
                  <ArrowDownwardIcon color="action" />
                </IconButton>
                <IconButton aria-label="close" onClick={closeDrawer}>
                  <CloseIcon color="action" />
                </IconButton>
              </Grid>
            }
            slotProps={{
              title: { variant: "body1" }
            }}
          />
        </Box>
        <Divider />
        {isLoading && (
          <div style={{ textAlign: "center", paddingTop: 3 }}>
            <ShiSpinnerIcon />
          </div>
        )}
        {!article && !isLoading && (
          <Typography sx={{ textAlign: "center", paddingTop: 3 }}>
            Article not found
          </Typography>
        )}
        {article && !isLoading && (
          <Box p={3}>
            <NewsCenterHeader
              title={article.title ?? ""}
              tags={article.tags ?? []}
            />
            <Grid container gap={3} key={articleId}>
              <Grid item container direction={"column"} xs={8} rowGap={3}>
                <Box
                  sx={{
                    "& img": { maxWidth: "100%" },
                  }}
                  className={"w-full"}
                >
                  {parse(sanitizedContent, {
                    replace: replaceLinksWithNewTab,
                  })}
                </Box>
                <Box
                  bgcolor="background.default"
                  width={"100%"}
                  px={2}
                  py={1}
                  alignItems={"center"}
                >
                  <Typography variant="body2">
                    Last updated {getModifiedTime()}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs>
                <Grid item container flexDirection="column" gap={3}>
                  <Grid item container flexDirection="column" gap={1}>
                    <div style={styles.sideBarListItemHeader}>Category</div>
                    <div>
                      <ColoredChips
                        label={article.category ?? ""}
                        borderColor={
                          categoryColors[
                            article.category
                              ? article.category.toLowerCase()
                              : "unknown"
                          ]
                        }
                      />
                    </div>
                  </Grid>

                  <Grid item container flexDirection="column" gap={1}>
                    <div style={styles.sideBarListItemHeader}>
                      Partner / Service
                    </div>
                    <div>
                      <Chip label={article.partner ?? ""} variant="outlined"/>
                    </div>
                  </Grid>

                  <Grid item container flexDirection="column" gap={1}>
                    <div style={styles.sideBarListItemHeader}>Impact</div>
                    <div>
                      <ColoredChips
                        label={article.severityLevel ?? ""}
                        borderColor={
                          severityLevelColors[
                            article.severityLevel
                              ? article.severityLevel.toLowerCase()
                              : "unknown"
                          ]
                        }
                      />
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
